/**
 * Contain a render-time failure and make it noticeable (CWS-5).
 *
 * Before this module any render throw above the lead-modal subtree unmounted
 * the whole tree — a blank white page, header and footer included, which is
 * how CWS-2's one broken card became a four-month whole-site outage. Two
 * boundaries share this class: the **page-level** one sits inside
 * ``LanguageRouter``'s atomic ``<Suspense>`` wrapping ``<main>`` (a page
 * failure keeps the header, the footer and the language switcher alive and
 * shows a localized apology in place of the page body), and the
 * **router-level** one sits in ``App.tsx`` around ``<AppRoutes />`` for the
 * throws the page boundary cannot reach — ``LanguageRouter``'s own render
 * (the FAQ resolution ``useMemo`` runs before any JSX exists, so a boundary
 * inside it is born too late; CWS-115's scope correction) and the chrome
 * components themselves. The router fallback is deliberately plain English
 * and dependency-free: the locale bundle may be the thing that failed.
 *
 * **Reset is scoped to failure, deliberately.** A failed boundary clears
 * itself when its ``resetKey`` changes *while failed* — a navigation to
 * another page after a caught error. A healthy navigation is untouched: a
 * ``key={pathname}`` remount was rejected because the pathname changes on
 * every language switch and every URL rescue, and remounting the page for
 * those would destroy page-local state and re-run mount effects that today
 * run once.
 *
 * **Recovery boundaries.** A rejected lazy-route chunk is caught here too,
 * but React.lazy caches the rejection and re-throws without re-importing
 * (the analysis recorded on ``LeadModalBoundary``), so navigation cannot
 * recover that class — the installed ``vitePreloadRecovery`` owns it, via
 * its session-latched reload on ``vite:preloadError``. Synchronous
 * ``useEffect`` throws are also routed here by React 18; async effect
 * rejections are not, and the CWS-199 locale-loader paths handle their own.
 * Failures above the router boundary (the providers, the bootstrap in
 * ``main.tsx``) remain uncaught — the ticket's title is scoped to page and
 * router render failures.
 *
 * **Reporting is the owner's decision, taken in the ticket** (scope item 3):
 * every catch leaves one searchable console trace (the ``LeadModalBoundary``
 * tradition) and one Umami event. The send is wrapped in the lead dialogs'
 * house pattern — a throwing, rejecting or absent tracker must never break
 * the fallback. Umami caps event *names* at 50 characters and documents no
 * value cap, so ``pathname`` and ``message`` are truncated to this module's
 * own stated bounds.
 *
 * React requires error boundaries to be class components.
 */

import { Component, type ReactNode } from "react";

/** The Umami event name (19 characters; the vendor's cap is 50). */
export const RENDER_FAILURE_EVENT = "Render Error Caught";

/** The page-boundary trace prefix, searchable in console and CI logs. */
export const PAGE_FAILURE_PREFIX = "Page render failed:";

/** The router-boundary trace prefix, for the boundary above the router. */
export const ROUTER_FAILURE_PREFIX = "Router render failed:";

/** The longest ``pathname`` the report payload carries. */
export const MAX_REPORT_PATHNAME_CHARS = 200;

/** The longest error ``message`` the report payload carries. */
export const MAX_REPORT_MESSAGE_CHARS = 300;

/** Which boundary caught the error — mirrors the two wiring sites. */
export type RenderErrorSurface = "page" | "router";

interface RenderErrorBoundaryProps {
  /** Which boundary this instance is; decides the trace prefix. */
  surface: RenderErrorSurface;
  /** What renders in place of the children once a child has thrown. */
  fallback: ReactNode;
  /**
   * The navigation identity of the page under the boundary.
   *
   * Changed *while failed* clears the failed state; changed while healthy it
   * does nothing, which is what keeps language switches and URL rescues
   * re-rendering in place instead of remounting the page.
   */
  resetKey?: string;
  children: ReactNode;
}

interface RenderErrorBoundaryState {
  failed: boolean;
}

/**
 * Flatten a caught value to the string the report payload carries
 *
 * Args:
 *     error: Whatever the child threw — usually an ``Error``.
 *
 * Returns:
 *     The error's message, or the stringified value for a non-Error throw.
 */
function describeError(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return String(error);
}

/**
 * Clip a value to the payload bound, keeping the head (the locating part)
 *
 * Args:
 *     value: The string to clip.
 *     maxChars: The bound to enforce.
 *
 * Returns:
 *     The unchanged string when within the bound, else its first
 *     ``maxChars`` characters.
 */
function truncateForReport(value: string, maxChars: number): string {
  return value.length <= maxChars ? value : value.slice(0, maxChars);
}

/**
 * Send one Umami event without ever throwing or rejecting
 *
 * The lead dialogs' house pattern: a synchronous throw is caught, and a
 * thenable return is caught too, because a rejected send would sail past a
 * synchronous ``catch``.
 *
 * Args:
 *     surface: Which boundary caught — the payload's ``surface`` property.
 *     pathname: The live URL at the catch, already truncated.
 *     message: The error's message, already truncated.
 */
function trackRenderFailure(
  surface: RenderErrorSurface,
  pathname: string,
  message: string,
): void {
  try {
    const sent = window.umami?.track(RENDER_FAILURE_EVENT, {
      surface,
      pathname,
      message,
    });
    if (sent && typeof (sent as PromiseLike<unknown>).then === "function") {
      Promise.resolve(sent).catch((error) => {
        console.error("Render error reporting failed:", error);
      });
    }
  } catch (error) {
    console.error("Render error reporting failed:", error);
  }
}

export class RenderErrorBoundary extends Component<
  RenderErrorBoundaryProps,
  RenderErrorBoundaryState
> {
  state: RenderErrorBoundaryState = { failed: false };

  /**
   * Enter the failed state when a child's render throws
   *
   * Returns:
   *     The state that renders the fallback in place of the children.
   */
  static getDerivedStateFromError(): RenderErrorBoundaryState {
    return { failed: true };
  }

  /**
   * Trace the catch and report it once
   *
   * The prefix is searchable from logs the way ``LeadModalBoundary``'s is,
   * and the Umami event carries the URL and the message so a dashboard
   * reader can act without asking for a stack trace.
   *
   * Args:
   *     error: Whatever the child's render rejected with.
   */
  componentDidCatch(error: unknown): void {
    const prefix =
      this.props.surface === "router" ? ROUTER_FAILURE_PREFIX : PAGE_FAILURE_PREFIX;
    console.error(prefix, error);

    // Read the URL at catch time rather than carrying it as a prop: the
    // router-level boundary has no router-context owner to ask, and
    // ``window.location`` is equally true for both surfaces.
    trackRenderFailure(
      this.props.surface,
      truncateForReport(window.location.pathname, MAX_REPORT_PATHNAME_CHARS),
      truncateForReport(describeError(error), MAX_REPORT_MESSAGE_CHARS),
    );
  }

  componentDidUpdate(prevProps: RenderErrorBoundaryProps): void {
    // Reset only from the failed state: a healthy boundary whose resetKey
    // changed is an ordinary navigation and must keep re-rendering in place.
    if (this.state.failed && this.props.resetKey !== prevProps.resetKey) {
      this.setState({ failed: false });
    }
  }

  render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}
